import React from 'react';
import { PopoverConfirmOverlay } from '@UI-Elements/PopoverOverlays/PopoverConfirmOverlay';
import { IFutureOrder } from '@sp-types/FutureOrderType';
import { Material } from '@sp-types/materialType';
import { Product } from '@sp-types/productType';
import { formatToPriceInDollars, formatToShortString } from '@utils/FormatValue';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlowSummary, IFlowSummaryItem } from '../../../../components/UI-Elements/Forms/FlowSummery';
import { getUnitObj } from '../../../../utils/MassUnits/massUnitsUtils';
import { futureOrderTypeDictionary, isFutureOrderFilledCorrectly } from '../AddNewFutureOrderUtils';
import { deliveryDatesToString } from '../FillOrderDetails/ExpectedDeliveries/DeliveryDatesPick/FutureOrderDeliveryDatePicker';

interface Props {
  futureOrder: IFutureOrder;
  accountProducts?: Product[] | null;
  product?: Product;
  materialsMap: Record<string, Material>;
  pricesMap: Record<string, number>;
  resetFutureOrderState?: () => void;
  submitFutureOrder: () => void;
  isLoading?: boolean;
  showSubmit?: boolean;
}

export const FutureOrderSummary: React.FC<Props> = ({
  futureOrder,
  accountProducts,
  product,
  materialsMap,
  pricesMap,
  resetFutureOrderState,
  submitFutureOrder,
  showSubmit = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="h-full ">
      <FlowSummary
        title="Order Summary"
        isButtonsShown={futureOrder.status !== 'completed' && showSubmit}
        isEditMode={true}
        subTitle="Here are the highlights of your quote"
        onPrimaryClick={{
          text: 'Submit',
          function: submitFutureOrder, // bad practice...
          isDisabled: isFutureOrderFilledCorrectly(futureOrder),
        }}
        onSecondaryClick={{
          text: 'Cancel',
          function: () => setIsDialogOpen(true),
        }}
        dataToPresent={createFlowItemsFromFutureOrder(futureOrder, materialsMap, pricesMap, accountProducts, product)}
      />
      <PopoverConfirmOverlay
        title="Cancel Future Order"
        text="Warning: All changes will be deleted."
        handleCancel={() => {
          setIsDialogOpen(false);
        }}
        handleConfirm={() => {
          resetFutureOrderState && resetFutureOrderState();
          navigate('/dashboard');
        }}
        isDialogOpen={isDialogOpen}
      />
    </div>
  );
};

const createFlowItemsFromFutureOrder = (
  { orderDetails, futureOrderType }: IFutureOrder,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  accountProducts?: Product[] | null,
  product?: Product,
): IFlowSummaryItem[] => {
  const { deliveryDates, quantity, pricePerUnit } = orderDetails;

  return [
    {
      title: 'Product',
      placeHolder: 'Choose product',
      value: product?.name,
    },
    {
      title: 'Product quantity',
      placeHolder: 'Product quantity',
      value: quantity ? quantity.toLocaleString() : undefined,
    },
    {
      title: 'Price per unit',
      placeHolder: 'Add price per unit',
      value: pricePerUnit ? formatToPriceInDollars(pricePerUnit) : undefined,
    },
    {
      title: 'Material(s)',
      placeHolder: 'Choose product',
      value: product?.ingredients?.map(
        ({ materialID }) =>
          `${materialsMap[materialID].name || materialID} , (${formatToPriceInDollars(pricesMap[materialID])})`,
      ),
    },
    {
      title: 'Materials Quantity',
      placeHolder: 'Set materials',
      value: accountProducts
        ? accountProducts
            .find((item) => item.uuid === product?.uuid)
            ?.ingredients.map(
              (item) =>
                `${formatToShortString(item.weightInProduct * quantity)} ${
                  getUnitObj(materialsMap[item.materialID].unit).shortForm
                }`,
            )
        : '',
    },
    {
      title: 'Contract Type',
      placeHolder: 'Select Contract type',
      value: futureOrderType && futureOrderTypeDictionary[futureOrderType].displayName,
    },

    {
      title: 'Delivery dates',
      placeHolder: 'set delivery range',
      value: deliveryDates ? deliveryDatesToString(deliveryDates) : undefined,
    },
    {
      title: 'Additional Details',
      placeHolder: '-',
      value: undefined,
    },
    {
      title: 'Total expected price',
      placeHolder: 'Set product and quantity',
      value: pricePerUnit ? formatToPriceInDollars(quantity * pricePerUnit) : undefined,
    },
  ];
};
